import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import { handleAPIError, queryClient } from "utils";
import {
  createWeighBill,
  deleteWeighBill,
  getWeighBillById,
  getWeighBills,
  updateWeighBill,
  updateWeighBillStatus,
} from "services/weighbill.service";
import { WeighbillType } from "types";
import { toast } from "react-toastify";

type UpdateWeighBillPayload = {
  id: string;
  data: WeighbillType;
};

type UpdateWeighBillStatusPayload = {
  id: string;
  data: { status: string };
};

// Fetch all weighbills
export const useGetWeighBills = () => {
  return useQuery({
    queryKey: [queryKeys.weighbill],
    queryFn: async () => await getWeighBills(),
  });
};

// Fetch a single weighbill by ID
export const useGetWeighBillyId = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.weighbill, id],
    queryFn: async () => await getWeighBillById(id),
    enabled: !!id,
  });
};

// Create a new weighbill
export const useCreateWeighBill = () => {
  return useMutation<any, Error, FormData>({
    mutationFn: createWeighBill,
    onSuccess: async (data: any) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.weighbill],
      });
    },
    onError: (error) => {
      console.error("Error creating weighbill:", error);
    },
  });
};

// / Update weighbill hook
export const useUpdateWeighBill = (): UseMutationResult<
  any,
  Error,
  UpdateWeighBillPayload
> => {
  return useMutation<any, Error, UpdateWeighBillPayload>({
    mutationFn: ({ id, data }: UpdateWeighBillPayload) =>
      updateWeighBill(id, data),
    onSuccess: async () => {
      // Invalidate weighbill query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.weighbill],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// / Update weighbill hook
export const useUpdateWeighBillStatus = (): UseMutationResult<
  any,
  Error,
  UpdateWeighBillStatusPayload
> => {
  return useMutation<any, Error, UpdateWeighBillStatusPayload>({
    mutationFn: ({ id, data }: UpdateWeighBillStatusPayload) =>
      updateWeighBillStatus(id, data),
    onSuccess: async () => {
      // Invalidate weighbill query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.weighbill],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a weighbill
export const useDeleteWeighBill = () => {
  return useMutation({
    mutationFn: deleteWeighBill,
    onSuccess: async () => {
      toast.success("Weighbill deleted successfully");
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.weighbill],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
